/**
 * Sign in. SPEC.md §7.0, reworked 29 Jul 2026 at the owner's direction.
 *
 * ONE way in: an identity, and then whatever that identity needs.
 *
 *   - The identity field takes a MOBILE NUMBER OR AN EMAIL — whichever the
 *     member remembers. Both resolve to the same account, and the same
 *     password works either way (see data/accounts.js).
 *   - The identity comes FIRST (owner's direction, 13 Sep 2026). `POST
 *     /auth/identify` asks the roster what to do with it, and the form becomes
 *     one of three things: ask for the password; offer to SET a first one; or
 *     say the committee has not activated this membership yet. A member added
 *     and activated this morning has no password and no reason to know what an
 *     "invitation email" is — asking them for a password they never had was a
 *     dead end, and the invitation they were emailed is the thing most likely
 *     to have been lost.
 *   - Setting that first password needs the OTHER identity on their record as
 *     proof (the mobile if they typed the email, and the reverse). Without it
 *     an email address would be enough to walk into somebody's account.
 *   - "Forgot password" emails a one-time link to the address on the record.
 *
 * The one-time SMS code is GONE. Every code cost the club money and forced
 * the Firebase Blaze plan (CLAUDE.md §8); email costs nothing. Anyone who
 * would have reached for the OTP — no password set, or forgotten — is served
 * by the reset link instead, which ends in the same place: a password.
 *
 * The roster decides everything. An identity mid-way through the join
 * pipeline (added / link sent / paid, but not yet activated by the committee)
 * is told "awaiting committee approval" — it cannot sign in early.
 *
 * There is NO self-service "Request membership" door here any more (owner's
 * direction, 27 Aug 2026): a committee adds members, and the runner's first
 * contact with the app is the one-time link that lands in their inbox. This
 * screen is now only what its name says — a way back IN for someone who is
 * already on a roster. `components/sheets/JoinSheet.jsx` was that door and has
 * no caller left; the public `POST /requests` route and the admin Requests
 * queue still exist for records already in flight.
 *
 * There is NO portal toggle (removed 29 Jul 2026, owner's direction). It asked
 * the member a question only the roster can answer, and it was pure friction:
 * `role: 'admin'` on the record opens the committee portal, and nothing a
 * visitor can click changes that. One door, one form, two possible landings.
 *
 * The screen also no longer advertises the seed password or explains that
 * "Forgot password" can set a first one. The club sends real email now; a real
 * app does not print its own credentials on the front door.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { identify } from '../data';
import { api } from '../lib/api';
import { DEMO_ACCOUNTS, useDemoAccounts } from '../lib/demo';
import { useAuth } from '../context/AuthContext';
import { useClub } from '../context/ClubContext';
import { useUI } from '../context/UIContext';
import { RunSplash } from '../components/RunSplash';
import { RunnerCrewMark } from '../components/ui';

/** Spinner + minimum busy time so a same-tick resolve still reads as work. */
const MIN_BUSY_MS = 450;
const SPLASH_MS = 850;

/**
 * The entry screens moved onto the marketing surface (26 Aug 2026): Archivo,
 * zero radius, the `line` / `gold` / `ink` tokens. The app's own forms inside
 * the club and console shells are untouched — they keep the rounded slate look.
 */
export const INPUT =
  'w-full border border-line-strong bg-white py-3 pl-11 pr-4 font-market text-[14.5px] text-ink-700 outline-none transition-colors duration-200 placeholder:text-copy-label/70 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/25';

/**
 * What the identity field says when it is empty.
 *
 * It used to show two sample values — `81970 91531 · you@email.com` — which
 * read as data already in the box rather than an instruction, and the sample
 * number was the committee's real one. An empty field should ask for
 * something. Both screens that take an identity share this one string so they
 * cannot drift apart.
 */
export const IDENTIFIER_PLACEHOLDER = 'Enter your mobile number or email';

/**
 * The platform mark. It is a LINK to the home page on every entry screen
 * (owner's direction, 27 Aug 2026) — the "← Back to the home page" text link
 * that used to sit at the foot of the sign-in card was removed, and the logo
 * is the way out in its place. That is also where a logo normally goes, so it
 * costs no explaining.
 */
export function Brand({ dark }) {
  return (
    <Link
      to="/"
      aria-label="RunnerCrew home"
      className="inline-flex items-center gap-2.5 transition-opacity duration-200 hover:opacity-80"
    >
      <RunnerCrewMark size={34} />
      <span className="leading-none">
        <span
          className={`block font-market text-[16.5px] font-extrabold tracking-[-0.015em] ${
            dark ? 'text-white' : 'text-ink-700'
          }`}
        >
          RunnerCrew
        </span>
        <span className="mt-[3px] block font-market text-[8.5px] font-bold uppercase tracking-[0.32em] text-gold-500/90">
          Club platform
        </span>
      </span>
    </Link>
  );
}

export function SignIn() {
  const { signIn, loading } = useClub();
  const { adoptSession } = useAuth();
  const { toast } = useUI();
  const navigate = useNavigate();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(''); // amber info (awaiting approval)
  const [busy, setBusy] = useState(false);
  /*
   * The identity comes FIRST and the roster decides what to ask next:
   *   'identity' → who are you?
   *   'password' → an account with a password. The old screen.
   *   'setup'    → activated, never signed in. Confirm one detail, choose a
   *                password, walk in — no invitation email to hunt for.
   * `who` holds what /auth/identify answered: the first name, and for setup
   * which detail to confirm plus a hint at the value we hold.
   */
  const [step, setStep] = useState('identity');
  const [who, setWho] = useState(null);
  const [confirmId, setConfirmId] = useState(''); // the other identity, typed back
  const [repeat, setRepeat] = useState(''); // the new password, twice
  const [splashName, setSplashName] = useState(null);
  /* The published read-only logins — drawn at once, removed only if the API
     says the demo was never seeded (lib/demo.js). */
  const demo = useDemoAccounts();

  /*
   * `?demo=admin` / `?demo=member` — the home page's "Open the demo club"
   * buttons land here rather than signing anyone in themselves, so the
   * splash, the toast and the landing decision stay in ONE place (`attempt`).
   * The visitor never sees this screen: while the param is being honoured the
   * render below holds a full-screen "opening the demo" frame instead of the
   * form (owner's direction, 2 Sep 2026 — clicking Open on the home page must
   * not flash a sign-in form). Fires once; a stale or unseeded param, or a
   * failed attempt, drops the hold and falls through to the ordinary form.
   */
  const wantedDemo = new URLSearchParams(useLocation().search).get('demo');
  const autoOpened = useRef(false);
  const [demoUnavailable, setDemoUnavailable] = useState(false);
  useEffect(() => {
    if (autoOpened.current || !wantedDemo) return;
    // The constants, not the API's answer: waiting for that meant waiting out
    // a cold start before the sign-in could even begin.
    const account = DEMO_ACCOUNTS.find((a) => a.role === wantedDemo);
    if (!account) {
      autoOpened.current = true;
      setDemoUnavailable(true); // let the form take over
      return;
    }
    if (busy || loading) return;
    autoOpened.current = true;
    openDemo(account);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedDemo, busy, loading]);

  function openDemo(account) {
    if (busy || loading) return;
    clearFeedback();
    // Straight past the identity step: these credentials came from the demo
    // card, not from anything the visitor typed.
    setStep('password');
    setIdentifier(account.email);
    setPassword(account.password);
    void attempt(account.email, account.password);
  }

  function clearFeedback() {
    setError('');
    setNotice('');
  }

  /** Back to "who are you?" — a wrong number typed, or the wrong account. */
  function startOver() {
    clearFeedback();
    setStep('identity');
    setWho(null);
    setPassword('');
    setConfirmId('');
    setRepeat('');
  }

  /*
   * The ROSTER decides everything, and the roster lives behind the API.
   *
   * This screen used to resolve the identity and check the password itself,
   * against a club held in the browser. It cannot any more, and should not: a
   * browser that can look up which email belongs to a phone number hands that
   * lookup to anyone with a phone book. So one call goes out with whatever was
   * typed, and the API answers with a session or a reason.
   *
   * The reasons arrive as error CODES, because they need different treatment.
   * AWAITING_APPROVAL is not a failure — it is someone who paid yesterday and
   * is being told to wait, so it shows amber, not red. NO_PASSWORD sends them
   * to the reset link rather than letting them guess at a password that does
   * not exist yet.
   */
  async function submit(e) {
    e?.preventDefault();
    if (busy || loading) return;
    clearFeedback();

    if (!identifier.trim()) {
      setError('Enter your mobile number or the email address on your membership.');
      return;
    }
    if (step === 'identity') return askTheRoster(identifier.trim());
    if (step === 'setup') return createPassword();
    if (!password) {
      setError('Enter your password.');
      return;
    }
    await attempt(identifier.trim(), password);
  }

  /**
   * Step one: hand the identity to the roster and do what it says.
   *
   * A member the committee added and activated has no password and never had
   * an account of their own to remember — so being asked for a password is
   * being asked for something that does not exist. The API answers with the
   * question that fits them instead (owner's direction, 13 Sep 2026).
   */
  async function askTheRoster(id) {
    setBusy(true);
    let answer;
    try {
      [answer] = await Promise.all([
        api.identifyAccount(id),
        new Promise((resolve) => setTimeout(resolve, MIN_BUSY_MS)),
      ]);
    } catch (err) {
      setError(err?.message ?? 'Could not check that. Try again.');
      setBusy(false);
      return;
    }
    setBusy(false);
    setWho(answer);
    if (answer.step === 'pending') {
      // Not a failure — somebody whose committee has not finished with them.
      setNotice(answer.message);
      return;
    }
    setStep(answer.step);
  }

  /**
   * Step two, for a first-timer: the detail off their membership record, then
   * the password they choose. Succeeding signs them in — the API returns a
   * full session, exactly as accepting an invitation does.
   */
  async function createPassword() {
    if (!confirmId.trim()) {
      setError(
        who?.confirm?.kind === 'phone'
          ? 'Enter the mobile number your club has for you.'
          : 'Enter the email address your club has for you.',
      );
      return;
    }
    if (password.length < 8) {
      setError('Choose a password of at least 8 characters.');
      return;
    }
    if (password !== repeat) {
      setError('The two passwords do not match.');
      return;
    }
    setBusy(true);
    let payload;
    try {
      [payload] = await Promise.all([
        api.setFirstPassword(identifier.trim(), confirmId.trim(), password),
        new Promise((resolve) => setTimeout(resolve, MIN_BUSY_MS)),
      ]);
    } catch (err) {
      if (err?.code === 'AWAITING_APPROVAL') setNotice(err.message);
      else setError(err?.message ?? 'Could not set your password. Try again.');
      setBusy(false);
      return;
    }
    const landing = adoptSession(payload);
    await land(payload.user?.name, landing, 'Welcome to RunnerCrew');
  }

  /**
   * The last few seconds of every way in: the running splash, a greeting, and
   * the landing the session asked for. Shared so a first password and a
   * hundredth sign-in end identically.
   */
  async function land(fullName, landing, greeting) {
    const firstName = fullName?.split(' ')[0] ?? 'runner';
    setSplashName(firstName);
    await new Promise((resolve) => setTimeout(resolve, SPLASH_MS));
    toast(`${greeting}, ${firstName}`);
    // One club and nothing else → straight in (the shell routes by role).
    // Several clubs, an owned portal or the platform console → the selector.
    //
    // `fromSignIn` is what lets the selector animate its entrance HERE and
    // nowhere else: it is also reached from "Switch club" in both shells, and
    // replaying the stagger on every visit turns a welcome into a wait
    // (owner's direction, 26 Aug 2026).
    navigate(landing.kind === 'club' ? '/' : '/select-club', { state: { fromSignIn: true } });
    window.scrollTo(0, 0);
  }

  /**
   * The one way in. Split out of `submit` so a demo card can drive it with
   * credentials the visitor never typed — the alternative was filling the two
   * inputs and firing a synthetic submit, which races React's state.
   */
  async function attempt(id, pw) {
    setBusy(true);

    let session;
    try {
      // Hold the spinner briefly even on a fast reply, so the tap reads as
      // acknowledged rather than as nothing having happened.
      [session] = await Promise.all([
        signIn(id, pw),
        new Promise((resolve) => setTimeout(resolve, MIN_BUSY_MS)),
      ]);
    } catch (err) {
      if (err?.code === 'AWAITING_APPROVAL') setNotice(err.message);
      else setError(err?.message ?? 'Could not sign you in. Try again.');
      setBusy(false);
      return;
    }

    await land(session.name, session.landing, 'Welcome back');
  }

  if (splashName) return <RunSplash name={splashName} />;

  // A demo link from the home page: hold a quiet frame while the account signs
  // itself in. An error drops the hold so the form can explain what went wrong.
  if (wantedDemo && !demoUnavailable && !error && !notice) return <DemoOpening />;

  return (
    <div className="relative grid min-h-screen grid-cols-1 bg-ground font-market lg:grid-cols-[1.05fr_1fr]">
      {/* left brand panel — desktop only. Same ink surface and drifting glow
          as the marketing hero, so the front door and the home page read as
          one product rather than two. */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-[linear-gradient(168deg,#161C2C_0%,#0D1220_46%,#0A0E19_100%)] p-9 text-white lg:flex">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-40 -top-48 h-[620px] w-[620px] rounded-full bg-[radial-gradient(circle,rgba(246,192,25,.16),transparent_65%)] motion-safe:animate-drift"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-56 -left-32 h-[540px] w-[540px] rounded-full bg-[radial-gradient(circle,rgba(14,124,112,.28),transparent_65%)] motion-safe:animate-drift-slow"
        />

        <div className="relative">
          <Brand dark />
        </div>

        <div className="relative max-w-[440px]">
          <span className="inline-flex items-center gap-2 border border-gold-500/35 bg-gold-500/[.08] px-3.5 py-[7px]">
            <span className="h-1.5 w-1.5 bg-gold-500" />
            <span className="font-market text-[10.5px] font-bold uppercase tracking-[0.24em] text-gold-500">
              Every club · one platform
            </span>
          </span>

          <h2 className="mt-6 font-market text-[clamp(30px,3.4vw,46px)] font-extrabold leading-[1.04] tracking-[-0.035em]">
            The streets are the stadium.
            <br />
            <span className="bg-[linear-gradient(100deg,#FFE383,#F6C019_46%,#7FD8C8)] bg-clip-text text-transparent">
              This is the office.
            </span>
          </h2>

          <p className="mt-5 max-w-[380px] font-market text-[14.5px] leading-[1.72] text-white/[.66]">
            One place for membership, dues, race results and volunteer hours —
            for committees and every runner, in every club on RunnerCrew.
          </p>
        </div>

        {demo.length > 0 ? (
          <DemoPanel accounts={demo} onOpen={openDemo} busy={busy || loading} />
        ) : (
          <div className="relative flex gap-10">
            {[
              ['1', 'Login'],
              ['∞', 'Clubs'],
              ['100%', 'On record'],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-market text-[28px] font-extrabold leading-none">{n}</div>
                <div className="mt-2 font-market text-[10px] font-semibold uppercase tracking-[0.16em] text-white/45">
                  {l}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* right form panel */}
      <div className="flex items-start justify-center px-5 py-9 sm:items-center sm:py-12">
        <div className="w-full max-w-[380px]">
          <div className="mb-9 lg:hidden">
            <Brand />
          </div>

          <h1 className="font-market text-[30px] font-extrabold leading-none tracking-[-0.03em] text-ink-700">
            {step === 'setup' ? 'Set your password' : 'Sign in'}
          </h1>
          <p className="mt-2.5 font-market text-[13.5px] leading-[1.6] text-copy-muted">
            {step === 'identity' &&
              'Start with your mobile number or email. Members and committee use the same form.'}
            {step === 'password' && 'Welcome back. Enter your password to continue.'}
            {step === 'setup' && (
              <>
                First time here{who?.name ? `, ${who.name}` : ''} — your club has you on the
                roster. Confirm one detail and choose a password.
              </>
            )}
          </p>

          <form onSubmit={submit} className="mt-7">
            <IdentifierField
              value={identifier}
              setValue={setIdentifier}
              onChange={clearFeedback}
              locked={step !== 'identity'}
              onUnlock={startOver}
            />

            {step === 'password' && (
              <>
                <PasswordField
                  id="g-pass"
                  label="Password"
                  autoComplete="current-password"
                  value={password}
                  setValue={setPassword}
                  onChange={clearFeedback}
                  show={showPw}
                  toggle={() => setShowPw((v) => !v)}
                />

                {/* Carries whatever they typed to the reset screen, so nobody
                    has to remember their own number twice in ten seconds. */}
                <div className="mb-5 flex justify-end">
                  <Link
                    to={identifier.trim() ? `/forgot?id=${encodeURIComponent(identifier.trim())}` : '/forgot'}
                    className="font-market text-[12px] font-semibold text-copy-muted underline underline-offset-2 transition-colors hover:text-gold-800"
                  >
                    Forgot password?
                  </Link>
                </div>
              </>
            )}

            {step === 'setup' && (
              <>
                {/* The proof. One detail off the record the committee filled
                    in — the identity they did NOT type — because without it an
                    email address would be a way into somebody's account. */}
                <div className="mb-4">
                  <label
                    htmlFor="g-confirm"
                    className="mb-2 block font-market text-[10px] font-bold uppercase tracking-[0.16em] text-copy-label"
                  >
                    {who?.confirm?.kind === 'phone'
                      ? 'Your mobile number'
                      : 'Your email address'}
                  </label>
                  <input
                    id="g-confirm"
                    type="text"
                    inputMode={who?.confirm?.kind === 'phone' ? 'tel' : 'email'}
                    autoComplete="off"
                    autoFocus
                    placeholder={
                      who?.confirm?.kind === 'phone' ? '98765 43210' : 'you@example.com'
                    }
                    className={`${INPUT} pl-4`}
                    value={confirmId}
                    onChange={(e) => {
                      setConfirmId(e.target.value);
                      clearFeedback();
                    }}
                  />
                  <p className="mt-2 font-market text-[11.5px] leading-[1.6] text-copy-label">
                    {who?.confirm?.kind === 'phone'
                      ? `The number on your membership — it ends ${who?.confirm?.hint ?? '••'}.`
                      : `The address on your membership — ${who?.confirm?.hint ?? ''}.`}
                  </p>
                </div>

                <PasswordField
                  id="g-new"
                  label="Choose a password"
                  autoComplete="off"
                  value={password}
                  setValue={setPassword}
                  onChange={clearFeedback}
                  show={showPw}
                  toggle={() => setShowPw((v) => !v)}
                  hint="At least 8 characters."
                />
                <PasswordField
                  id="g-repeat"
                  label="Type it again"
                  autoComplete="off"
                  value={repeat}
                  setValue={setRepeat}
                  onChange={clearFeedback}
                  show={showPw}
                />
              </>
            )}

            <SubmitButton
              busy={busy}
              label={
                step === 'identity'
                  ? 'Continue'
                  : step === 'setup'
                    ? 'Create password & sign in'
                    : 'Sign in'
              }
              busyLabel={step === 'setup' ? 'Setting up…' : 'Checking…'}
              disabled={loading}
            />
            <Feedback error={error} notice={notice} />

            {/* A member whose committee has not activated them yet gets the
                amber notice above and nothing to do here but come back. */}
            {step === 'identity' && notice && (
              <div className="mt-3 text-center">
                <Link
                  to="/forgot"
                  className="font-market text-[12px] font-semibold text-copy-muted underline underline-offset-2 transition-colors hover:text-gold-800"
                >
                  Already had a password?
                </Link>
              </div>
            )}
          </form>

          {demo.length > 0 && (
            <div className="mt-8 lg:hidden">
              <DemoPanel accounts={demo} onOpen={openDemo} busy={busy || loading} light />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * One field, two identities.
 *
 * Two separate inputs (or a tabbed toggle) would ask the member a question
 * they should not have to answer — "which of my two logins is this?" — so the
 * field takes either and `identify()` works out which. That reading drives the
 * leading icon and the keyboard, nothing else.
 *
 * There used to be an echo underneath repeating what we understood — the
 * tidied number or address. It was removed on the owner's direction
 * (26 Aug 2026): printing the address back at someone who just typed it tells
 * them nothing they cannot see in the field an inch above, and on a signed-out
 * screen it puts an account's email on display for whoever is looking.
 */
function IdentifierField({ value, setValue, onChange, locked, onUnlock }) {
  const { kind } = identify(value);

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <label
          htmlFor="g-user"
          className="block font-market text-[10px] font-bold uppercase tracking-[0.16em] text-copy-label"
        >
          Mobile number or email
        </label>
        {/* Locked once the roster has answered for it — changing it asks a
            different account's question, so it starts the form again. */}
        {locked && (
          <button
            type="button"
            onClick={onUnlock}
            className="font-market text-[11px] font-semibold text-copy-muted underline underline-offset-2 transition-colors hover:text-gold-800"
          >
            Not you?
          </button>
        )}
      </div>
      <div className="relative flex items-center">
        {kind === 'email' ? (
          <svg
            className="pointer-events-none absolute left-3.5"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6C7382"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
            <path d="m3 7 9 6 9-6" />
          </svg>
        ) : (
          <svg
            className="pointer-events-none absolute left-3.5"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6C7382"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <rect x="6" y="2" width="12" height="20" rx="2.5" />
            <path d="M11 18.5h2" />
          </svg>
        )}
        <input
          id="g-user"
          type="text"
          // ALWAYS the email keyboard. Switching on `kind` opened an empty
          // field on the number pad, which has no "@" — so an email could
          // never be typed and the switch never fired. The email keyboard
          // still reaches digits in one tap.
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoComplete="username"
          autoFocus={!locked}
          readOnly={locked}
          placeholder={IDENTIFIER_PLACEHOLDER}
          className={`${INPUT} ${locked ? 'bg-ground text-copy-muted' : ''}`}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            onChange();
          }}
        />
      </div>
    </div>
  );
}

/**
 * A password input with the lock icon and the show/hide toggle. Three of them
 * on this screen now (sign in, choose, repeat) — one component so the
 * first-timer's boxes cannot drift from the returning member's.
 */
function PasswordField({ id, label, autoComplete, value, setValue, onChange, show, toggle, hint }) {
  return (
    <div className="mb-4">
      <label
        htmlFor={id}
        className="mb-2 block font-market text-[10px] font-bold uppercase tracking-[0.16em] text-copy-label"
      >
        {label}
      </label>
      <div className="relative flex items-center">
        <svg
          className="pointer-events-none absolute left-3.5"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#6C7382"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <rect x="4" y="10" width="16" height="11" rx="2.5" />
          <path d="M8 10V7a4 4 0 0 1 8 0v3" />
        </svg>
        <input
          id={id}
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          placeholder="••••••••"
          className={INPUT}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            onChange();
          }}
        />
        {toggle && (
          <button
            type="button"
            className="absolute right-2 px-2 py-1.5 font-market text-[10px] font-bold uppercase tracking-[0.12em] text-copy-label transition-colors hover:text-ink-700"
            onClick={toggle}
          >
            {show ? 'Hide' : 'Show'}
          </button>
        )}
      </div>
      {hint && (
        <p className="mt-2 font-market text-[11.5px] leading-[1.6] text-copy-label">{hint}</p>
      )}
    </div>
  );
}

export function SubmitButton({ busy, label, busyLabel, disabled }) {
  return (
    <button
      type="submit"
      disabled={busy || disabled}
      className="flex w-full items-center justify-center gap-2 bg-[linear-gradient(140deg,#FFD75E,#F6C019_58%,#EFB000)] py-4 font-market text-[15px] font-extrabold text-ink-800 shadow-[0_12px_26px_-14px_rgba(246,192,25,.9)] transition-all duration-200 hover:-translate-y-px hover:brightness-105 active:translate-y-0 disabled:opacity-70"
    >
      {busy && (
        <span
          className="h-4 w-4 flex-none animate-spin rounded-full border-2 border-ink-800/40 border-t-ink-800 motion-reduce:animate-pulse"
          aria-hidden="true"
        />
      )}
      {busy ? busyLabel : label}
    </button>
  );
}

export function Feedback({ error, notice }) {
  return (
    <div className="mt-3 min-h-[16px]">
      {error && <div className="font-market text-[12.5px] font-semibold text-flag-500">{error}</div>}
      {notice && (
        <div className="border border-gold-100 bg-gold-50 px-3.5 py-3 font-market text-[12.5px] leading-relaxed text-gold-900">
          {notice}
        </div>
      )}
    </div>
  );
}

/**
 * What `/signin?demo=…` shows instead of the sign-in form: the same ink
 * surface as the marketing hero with a spinner, held for the second or two it
 * takes the demo account to sign itself in. The visitor clicked "Open" on the
 * home page — a form asking for credentials they never typed would read as a
 * wrong turn.
 */
function DemoOpening() {
  return (
    <div className="grid min-h-screen place-items-center bg-[linear-gradient(168deg,#161C2C_0%,#0D1220_46%,#0A0E19_100%)] font-market">
      <div className="flex flex-col items-center gap-6 text-center">
        <RunnerCrewMark size={42} />
        <span
          className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-gold-500 motion-reduce:animate-pulse"
          aria-hidden="true"
        />
        <div>
          <p className="font-market text-[16px] font-extrabold tracking-[-0.01em] text-white">
            Opening the demo club…
          </p>
          <p className="mt-2 font-market text-[12.5px] leading-relaxed text-white/50">
            Read-only — look at everything, change nothing.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * The published demo logins.
 *
 * Anyone evaluating the platform hits this screen with no account, and the
 * honest answer to "can I look first?" is a working login rather than a
 * screenshot or a sales call. Both accounts are READ-ONLY — enforced by the
 * API for every write, not by hiding buttons — so there is nothing to protect
 * about the password, and printing it is the whole point.
 *
 * `light` renders it for the white form column (phones, where the ink rail is
 * hidden); the default is the dark rail.
 */
function DemoPanel({ accounts, onOpen, busy, light }) {
  const frame = light
    ? 'border border-line bg-white'
    : 'relative border border-white/[.14] bg-white/[.05]';
  const kicker = light ? 'text-gold-800' : 'text-gold-500';
  const title = light ? 'text-ink-700' : 'text-white';
  const body = light ? 'text-copy-muted' : 'text-white/[.55]';
  const row = light
    ? 'border-t border-line first:border-t-0'
    : 'border-t border-white/[.12] first:border-t-0';
  const cred = light ? 'text-copy-label' : 'text-white/40';
  const cta = light
    ? 'border border-ink-700 text-ink-700 hover:bg-ink-700 hover:text-white'
    : 'bg-gold-500 text-ink-800 hover:brightness-110';

  return (
    <div className={`${frame} p-5`}>
      <div className={`font-market text-[10px] font-bold uppercase tracking-[0.24em] ${kicker}`}>
        See it working
      </div>
      <p className={`mt-1.5 font-market text-[15px] font-extrabold tracking-[-0.02em] ${title}`}>
        Open the demo club
      </p>
      <p className={`mt-1 font-market text-[12.5px] leading-[1.6] ${body}`}>
        A real club with members, events and results. Read-only — you can open
        every screen, nothing can be changed.
      </p>

      <div className="mt-3.5">
        {accounts.map((a) => (
          <div key={a.email} className={`${row} flex items-center gap-3 py-3`}>
            <div className="min-w-0 flex-1">
              <div className={`font-market text-[12.5px] font-bold ${title}`}>{a.label}</div>
              {/* wraps, never truncates — on a phone the ellipsis ate the password */}
              <div className={`font-market text-[11px] tabular-nums [overflow-wrap:anywhere] ${cred}`}>
                {a.email} · {a.password}
              </div>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={() => onOpen(a)}
              className={`flex-none px-3.5 py-2 font-market text-[11px] font-extrabold uppercase tracking-[0.12em] transition-all duration-200 disabled:opacity-50 ${cta}`}
            >
              Open
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
